// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Button,
  ListItem,
  Table,
  TableRow,
  TableCell,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function BillBackTransactionsPage() {
  const pageTitle = "Bill Back Promotions - Transactions";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [currency, setCurrency] = useState(null);

  const tableColumns = [
    { Header: "#", accessor: "view", align: "left" },
    { Header: "Wholesaler", accessor: "wholesaler", align: "left" },
    { Header: "Retailer", accessor: "retailer", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Date", accessor: "created", align: "right" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        retailer: row.outlet,
        wholesaler: row.wholesaler,
        status: row.status,
        view: (
          <MDBox>
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => {
                getTransaction(row.reference);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small">
                east
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/tm/promotions/bill_back/${ref}/transactions.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCurrency(result.data.currency);
            const rows = buildRateRows(result.data.data.transactions);
            setTransactions(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getTransaction(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/tm/promotions/bill_back/transactions/${reference}/fetch.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setTransaction(result.data.data.transaction);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {transaction && (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle>
            <MDBox align="center">
              <MDTypography variant="subtitle2" fontSize={14}>
                Transaction ID
              </MDTypography>
              <MDTypography variant="subtitle2">{transaction.reference}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox mt={3}>
              <Table>
                <TableRow>
                  <TableCell align="left">
                    <MDTypography variant="subtitle2">Wholesaler</MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="subtitle2">{transaction.wholesaler}</MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <MDTypography variant="subtitle2">Retailer</MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="subtitle2">{transaction.outlet}</MDTypography>
                  </TableCell>
                </TableRow>
                {transaction.items.map((row) => (
                  <TableRow>
                    <TableCell align="left">
                      <MDTypography variant="subtitle2">{row.product}</MDTypography>
                      <MDTypography
                        variant="subtitle2"
                        fontSize={12}
                      >{`Quantity: ${row.quantity}`}</MDTypography>
                      <MDTypography variant="subtitle2" fontSize={12}>{`Unit Price: ${currency}${(
                        row.unit_price / 100
                      ).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}</MDTypography>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="subtitle2">{`${currency}${(
                        row.total / 100
                      ).toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}</MDTypography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell align="left">
                    <MDTypography variant="subtitle2">Discount</MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="subtitle2">{`${currency}${(
                      transaction.discount / 100
                    ).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}</MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <MDTypography variant="subtitle2">Total</MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="subtitle2">{`${currency}${(
                      transaction.total / 100
                    ).toLocaleString("en", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}</MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <MDTypography variant="subtitle2">Date</MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="subtitle2">{transaction.created}</MDTypography>
                  </TableCell>
                </TableRow>
              </Table>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              iconOnly
              circular
              onClick={() => setTransaction(null)}
            >
              <Icon>close</Icon>
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      <MDBox mb={5}>
        <MDBox mt={1} mb={2} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/trade-marketing/promotions/bill-back/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox mt={2}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={0}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox p={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Transactions
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox mt={1}>
                  <DataTable
                    table={{ columns: tableColumns, rows: transactions }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BillBackTransactionsPage;
